import { useAuth0 } from "ct-auth"
import { keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import {
  commitMemberImport,
  compareGroups,
  compareGroupsScenarios,
  compareRuns,
  deleteKpiCustomFramework,
  deleteKpiDashboardView,
  exportKpiDashboard,
  exportKpiPerson,
  exportKpiReport,
  erasePersonData,
  fetchKpis,
  getKpiAccessLog,
  getKpiDashboardViews,
  getKpiFrameworks,
  getMemberImport,
  getOrgMemberProfiles,
  getRoleDepartments,
  getScenarioFrameworkMap,
  previewMemberImport,
  saveKpiCustomFramework,
  saveKpiDashboardView,
  setRoleDepartments,
  setScenarioAttackTypes,
  updateKpiFrameworkSettings,
  upsertOrgMembers,
} from "./data"
import { isManager, isSponsor, isTrainer, kpiAnalyticsEnabled, userOrg, userPermissions } from "./authUtils"
import { KPI_VIEW, canGrantKpiFor, kpiGrantPath, kpiViewFor } from "./kpiView"
import { useQueries } from "./reactQueryHelpers"

// KPI P1 Settings hooks (spec §11.1, §11.5). Mutations fire only from user events.
export const KPI_QUERY_KEYS = {
  ROLE_DEPARTMENTS: ({ userId, orgId, search, kind, status, sort }) => [
    "KPI_ROLE_DEPARTMENTS",
    userId,
    orgId ?? "own",
    kind,
    status,
    sort,
    search ?? "",
  ],
  COVERAGE: ({ userId, orgId }) => ["KPI_COVERAGE", userId, orgId ?? "own"],
}

const PAGE_SIZE = 50

export const useRoleDepartments = ({ orgId = null, search = "", kind = "role", status = "all", sort = "exercises", enabled = true } = {}) => {
  const { getAccessTokenSilently, user } = useAuth0()
  return useInfiniteQuery({
    queryKey: KPI_QUERY_KEYS.ROLE_DEPARTMENTS({ userId: user?.sub, orgId, search, kind, status, sort }),
    queryFn: async ({ pageParam }) => {
      const accessToken = await getAccessTokenSilently()
      return await getRoleDepartments(accessToken, user?.sub, {
        orgId: orgId ?? undefined,
        search: search || undefined,
        kind,
        status,
        sort,
        limit: PAGE_SIZE,
        cursor: pageParam ?? undefined,
      })
    },
    initialPageParam: null,
    getNextPageParam: (lastPage) => lastPage?.nextCursor ?? null,
    // Keep the current rows on screen while a new search/filter loads (no flash to empty).
    placeholderData: keepPreviousData,
    enabled: !!user?.sub && enabled,
    staleTime: 30 * 1000,
  })
}

// Server returns the touched entries ({ kind, roleKey, departmentId, source, needsReview }); fall
// back to the request's updates if it didn't (e.g. an older server).
const patchesFromSave = ({ result, updates }) => {
  const rows =
    Array.isArray(result?.entries) && result.entries.length
      ? result.entries
      : (updates ?? []).map((u) => ({ ...u, source: "sponsor", needsReview: false }))
  return new Map(
    rows
      .filter((r) => r?.kind && r?.roleKey)
      .map((r) => [
        `${r.kind}:${r.roleKey}`,
        { departmentId: r.departmentId ?? null, source: r.source ?? "sponsor", needsReview: r.needsReview === true, confidence: 1 },
      ])
  )
}

export const useSetRoleDepartments = () => {
  const queryClient = useQueryClient()
  const { getAccessTokenSilently, user } = useAuth0()
  return useMutation({
    mutationFn: async ({ orgId = null, updates, acceptSuggested }) => {
      const accessToken = await getAccessTokenSilently()
      return await setRoleDepartments(accessToken, user?.sub, { orgId: orgId ?? undefined, updates, acceptSuggested })
    },
    onSuccess: (result, { updates }) => {
      // Patch the saved rows into every loaded role-table page first, so a changed row keeps its
      // new department while the refetch is in flight instead of snapping back.
      const saved = patchesFromSave({ result, updates })
      if (saved.size) {
        queryClient.setQueriesData({ queryKey: ["KPI_ROLE_DEPARTMENTS"] }, (old) =>
          old?.pages
            ? {
                ...old,
                pages: old.pages.map((page) =>
                  page?.entries
                    ? {
                        ...page,
                        entries: page.entries.map((e) =>
                          saved.has(`${e.kind}:${e.roleKey}`) ? { ...e, ...saved.get(`${e.kind}:${e.roleKey}`) } : e
                        ),
                      }
                    : page
                ),
              }
            : old
        )
      }
      // Only the table on screen refetches now (the summary counts change); other cached
      // filter/search variants are marked stale and refetch when shown again.
      queryClient.invalidateQueries({ queryKey: ["KPI_ROLE_DEPARTMENTS"], refetchType: "active" })
      queryClient.invalidateQueries({ queryKey: ["KPI_COVERAGE"] })
      queryClient.invalidateQueries({ queryKey: ["KPI_DASHBOARD"] })
    },
  })
}

export const useKpiCoverage = ({ orgId = null, enabled = true } = {}) => {
  const { getAccessTokenSilently, user } = useAuth0()
  return useQuery({
    queryKey: KPI_QUERY_KEYS.COVERAGE({ userId: user?.sub, orgId }),
    queryFn: async () => {
      const accessToken = await getAccessTokenSilently()
      const response = await fetchKpis(accessToken, user?.sub, {
        orgId: orgId ?? undefined,
        queries: [
          { id: "coverage", metric: "meta.coverage" },
          { id: "dims", metric: "meta.dimensionValues" },
        ],
        options: { includeCoverage: false },
      })
      if (!response) return null
      const byId = Object.fromEntries((response.results ?? []).map((r) => [r.id, r.value]))
      return { coverage: byId.coverage ?? null, dims: byId.dims ?? null, asOf: response.asOf ?? null }
    },
    enabled: !!user?.sub && enabled,
    staleTime: 60 * 1000,
  })
}

// ─── KPI P2 (spec §12) ───────────────────────────────────────────────────────
// Every P2 surface shows only for orgs holding KPI_ANALYTICS (slow rollout); the server refuses
// the same calls with 403 KPI_FEATURE_NOT_ENABLED. `loading` holds UI decisions until the org is in.
export const useKpiEntitlement = ({ orgId = null } = {}) => {
  const { user } = useAuth0()
  const id = orgId ?? userOrg(user)
  const { data: org, isLoading } = useQueries.HOME_USER_ORG({ orgId: id, enabled: !!id })
  return { enabled: kpiAnalyticsEnabled(org), loading: !!id && isLoading, org: org ?? null }
}

// ─── KPI on an exercise (report, group dashboard, exercise row) ───────────────
// The rules live in kpiView.js (pure, so screens that mock this module keep them).
// orgId: the exercise's org; undefined while the exercise is still loading (nothing shows yet).
export const useExerciseKpi = ({ orgId } = {}) => {
  const { user } = useAuth0()
  const viewerOrgId = userOrg(user) || null
  const known = orgId !== undefined
  const targetOrgId = orgId || viewerOrgId
  const crossOrg = !!orgId && orgId !== viewerOrgId
  const manager = isManager(user)
  const sponsor = isSponsor(user)
  const trainer = isTrainer(user)
  const reachable = kpiViewFor({ manager, sponsor, trainer, crossOrg, kpi: true }) !== KPI_VIEW.NONE
  const { data: targetOrg, isLoading, isError } = useQueries.HOME_USER_ORG({ orgId: targetOrgId, enabled: known && reachable && !!targetOrgId })
  const permissions = userPermissions(user) ?? []
  const partnerGrant = !manager && sponsor && crossOrg && permissions.includes("organizations:crud:managed")
  const { data: viewerOrg } = useQueries.HOME_USER_ORG({ orgId: viewerOrgId, enabled: partnerGrant && !!viewerOrgId })
  const loading = !known || (reachable && !!targetOrgId && isLoading)
  // A failed or empty org read (e.g. an org outside the viewer's scope) shows nothing, never "KPI is off".
  const orgRead = !isError && !!targetOrg
  const view = loading ? KPI_VIEW.NONE : kpiViewFor({ manager, sponsor, trainer, crossOrg, kpi: kpiAnalyticsEnabled(targetOrg), orgRead })
  const canGrant = view === KPI_VIEW.EXPLAIN && !!targetOrgId && canGrantKpiFor({ manager, permissions, viewerOrgId, viewerOrg, targetOrg })
  return {
    view,
    loading,
    orgId: targetOrgId,
    orgName: targetOrg?.displayName || null,
    canGrant,
    grantPath: canGrant ? kpiGrantPath(targetOrgId) : null,
  }
}

const useAuthedFetch = () => {
  const { getAccessTokenSilently, user } = useAuth0()
  return { userId: user?.sub, call: async (fn, args) => fn(await getAccessTokenSilently(), user?.sub, args) }
}

export const P2_KEYS = {
  COMPARE_RUNS: (args) => ["KPI_COMPARE_RUNS", JSON.stringify(args)],
  COMPARE_GROUPS: (args) => ["KPI_COMPARE_GROUPS", JSON.stringify(args)],
  COMPARE_GROUPS_SCENARIOS: (args) => ["KPI_COMPARE_GROUPS_SCENARIOS", JSON.stringify(args)],
  PERSON: (args) => ["KPI_PERSON", JSON.stringify(args)],
  MY_PROGRESS: (userId) => ["KPI_MY_PROGRESS", userId],
  MEMBER_PROFILES: (args) => ["KPI_MEMBER_PROFILES", JSON.stringify(args)],
  ACCESS_LOG: (args) => ["KPI_ACCESS_LOG", JSON.stringify(args)],
}

// run: { sessionName } | { groupName }. summaryOnly powers the tab badge and the score card.
export const useCompareRuns = ({
  orgId = null,
  run,
  baseline = null,
  mode = "comparable",
  summaryOnly = false,
  against = "scenario",
  attackType = null,
  enabled = true,
} = {}) => {
  const { userId, call } = useAuthedFetch()
  // The default same-scenario request is unchanged (no against/attackType sent).
  const cross = against && against !== "scenario"
  const args = {
    orgId: orgId ?? undefined,
    run,
    baseline: baseline ?? undefined,
    mode,
    summaryOnly,
    ...(cross ? { against, attackType: attackType ?? undefined } : {}),
  }
  return useQuery({
    queryKey: [...P2_KEYS.COMPARE_RUNS(args), userId],
    queryFn: () => call(compareRuns, args),
    enabled: !!userId && !!run && enabled,
    staleTime: 60 * 1000,
    retry: (count, err) => count < 2 && ![400, 403, 404].includes(err?.status),
  })
}

export const useCompareGroups = ({ args, enabled = true } = {}) => {
  const { userId, call } = useAuthedFetch()
  return useQuery({
    queryKey: [...P2_KEYS.COMPARE_GROUPS(args), userId],
    queryFn: () => call(compareGroups, args),
    enabled: !!userId && !!args?.basis && !!args?.groupBy && enabled,
    // Keep the last result on screen while an option changes for the SAME scenario; a different
    // scenario shows the loading state instead of the previous scenario's title and bars.
    placeholderData: (previous) => (previous && previous.basis?.lineageId === args?.basis?.lineageId ? previous : undefined),
    staleTime: 60 * 1000,
    retry: (count, err) => count < 2 && ![400, 403, 404].includes(err?.status),
  })
}

// Compare departments' scenario picker: { setupType?, range } → { rule, scenarios: [{ ready, … }] }.
export const useCompareGroupsScenarios = ({ args, enabled = true } = {}) => {
  const { userId, call } = useAuthedFetch()
  return useQuery({
    queryKey: [...P2_KEYS.COMPARE_GROUPS_SCENARIOS(args), userId],
    queryFn: () => call(compareGroupsScenarios, args),
    enabled: !!userId && !!args && enabled,
    placeholderData: keepPreviousData,
    staleTime: 60 * 1000,
    retry: (count, err) => count < 2 && ![400, 403, 404].includes(err?.status),
  })
}

const personQuery = ({ orgId, filters, queries, options }) => ({
  orgId: orgId ?? undefined,
  ...(filters ? { filters } : {}),
  queries,
  ...(options ? { options } : {}),
})

// One page of the People directory (person.directory). Page N is fetched directly with a row
// offset ((N-1) * pageSize): one request per page, never the pages in between, never the whole
// directory. Each page is its own cached query; the query `signal` cancels a superseded fetch when
// the reader moves on before it lands. The result carries the `page` its rows belong to, so the
// footer can describe the rows on screen while the next page is still loading (keepPreviousData).
const PEOPLE_STALE_MS = 30 * 1000
export const peopleDirectoryKey = ({ userId, orgId = null, filters = {}, search = "", sort = "exercises", order = null, pageSize, page }) => [
  "KPI_PEOPLE",
  userId,
  orgId,
  JSON.stringify(filters),
  search,
  sort,
  order,
  pageSize,
  page,
]

export const usePeopleDirectoryPage = ({
  orgId = null,
  filters = {},
  search = "",
  sort = "exercises",
  order = null,
  page = 1,
  pageSize = 20,
  enabled = true,
} = {}) => {
  const { userId, call } = useAuthedFetch()
  return useQuery({
    queryKey: peopleDirectoryKey({ userId, orgId, filters, search, sort, order, pageSize, page }),
    queryFn: async ({ signal }) => {
      const offset = (Math.max(1, page) - 1) * pageSize
      const query = {
        id: "people",
        metric: "person.directory",
        sort,
        ...(order ? { order } : {}),
        ...(search ? { search } : {}),
        limit: pageSize,
        ...(offset ? { offset } : {}),
      }
      const response = await call(fetchKpis, { ...personQuery({ orgId, filters, queries: [query], options: { includeCoverage: false } }), signal })
      const result = response?.results?.[0] ?? null
      if (!result) return null
      const total = result.total ?? 0
      // Past the last page (the list shrank): the server answers an empty page with the real total.
      const pastEnd = offset > 0 && offset >= total
      return { ...result, page, privacy: response.privacy, ...(pastEnd ? { pastEnd: true } : {}) }
    },
    placeholderData: keepPreviousData,
    enabled: !!userId && enabled,
    staleTime: PEOPLE_STALE_MS,
    retry: (count, err) => count < 2 && ![400, 403, 404].includes(err?.status),
  })
}

export const usePersonProfile = ({ orgId = null, personRef, enabled = true } = {}) => {
  const { userId, call } = useAuthedFetch()
  return useQuery({
    queryKey: [...P2_KEYS.PERSON({ orgId, personRef }), userId],
    queryFn: async () => {
      const response = await call(
        fetchKpis,
        personQuery({
          orgId,
          filters: { person: [personRef] },
          queries: [{ id: "person", metric: "person.profile" }],
          options: { includeCoverage: false },
        })
      )
      const result = response?.results?.[0] ?? null
      return result ? { ...result, privacy: response.privacy ?? null } : null
    },
    enabled: !!userId && !!personRef && enabled,
    staleTime: 30 * 1000,
    retry: (count, err) => count < 2 && ![400, 403, 404].includes(err?.status),
  })
}

// My progress: own scope (the server resolves the caller; no filters).
export const useMyProgress = ({ enabled = true } = {}) => {
  const { userId, call } = useAuthedFetch()
  return useQuery({
    queryKey: P2_KEYS.MY_PROGRESS(userId),
    queryFn: async () =>
      (await call(fetchKpis, personQuery({ queries: [{ id: "person", metric: "person.profile" }], options: { includeCoverage: false } })))
        ?.results?.[0] ?? null,
    enabled: !!userId && enabled,
    staleTime: 60 * 1000,
    retry: (count, err) => count < 2 && ![400, 403, 404].includes(err?.status),
  })
}

// A named export (purpose: 'export'): refused unless the org allows names in exports; logged.
export const useExportPeople = () => {
  const { call } = useAuthedFetch()
  return useMutation({
    mutationFn: async ({ orgId = null, filters = {}, search = "", sort = "exercises", personRef = null }) => {
      const queries = personRef
        ? [{ id: "person", metric: "person.profile" }]
        : [{ id: "people", metric: "person.directory", sort, ...(search ? { search } : {}), limit: 100 }]
      const rows = []
      let cursor = null
      for (let page = 0; page < 50; page += 1) {
        const q = personRef ? queries : [{ ...queries[0], ...(cursor ? { cursor } : {}) }]
        const response = await call(
          fetchKpis,
          personQuery({
            orgId,
            filters: personRef ? { ...filters, person: [personRef] } : filters,
            queries: q,
            options: { includeCoverage: false, purpose: "export" },
          })
        )
        const result = response?.results?.[0]
        if (personRef) return result
        rows.push(...(result?.rows ?? []))
        cursor = result?.nextCursor ?? null
        if (!cursor) break
      }
      return { rows }
    },
  })
}

export const useMemberProfiles = ({ orgId = null, emails = null, missingGroupKey = null, enabled = true } = {}) => {
  const { userId, call } = useAuthedFetch()
  const args = { orgId: orgId ?? undefined, ...(emails ? { emails } : {}), ...(missingGroupKey ? { missingGroupKey } : {}) }
  return useQuery({
    queryKey: [...P2_KEYS.MEMBER_PROFILES(args), userId],
    queryFn: () => call(getOrgMemberProfiles, args),
    enabled: !!userId && enabled,
    staleTime: 30 * 1000,
  })
}

const invalidatePeople = (queryClient) => {
  for (const key of ["KPI_MEMBER_PROFILES", "KPI_PEOPLE", "KPI_PERSON", "KPI_DASHBOARD", "KPI_COMPARE_GROUPS", "KPI_COMPARE_GROUPS_SCENARIOS"])
    queryClient.invalidateQueries({ queryKey: [key] })
}

export const useUpsertMembers = () => {
  const queryClient = useQueryClient()
  const { call } = useAuthedFetch()
  return useMutation({
    mutationFn: ({ orgId = null, members, applyToHistory = false, confirmMerge = undefined }) =>
      call(upsertOrgMembers, { orgId: orgId ?? undefined, members, applyToHistory, confirmMerge }),
    onSuccess: () => invalidatePeople(queryClient),
  })
}

export const usePreviewImport = () => {
  const { call } = useAuthedFetch()
  return useMutation({ mutationFn: (args) => call(previewMemberImport, args) })
}

export const useCommitImport = () => {
  const queryClient = useQueryClient()
  const { call } = useAuthedFetch()
  return useMutation({
    mutationFn: (args) => call(commitMemberImport, args),
    onSuccess: () => invalidatePeople(queryClient),
  })
}

// Status polling is a query (allowed in an effect): every 5 s while history is still filling.
export const useMemberImportStatus = ({ orgId = null, importId, enabled = true } = {}) => {
  const { userId, call } = useAuthedFetch()
  return useQuery({
    queryKey: ["KPI_IMPORT_STATUS", userId, orgId, importId],
    queryFn: () => call(getMemberImport, { orgId: orgId ?? undefined, importId }),
    enabled: !!userId && !!importId && enabled,
    refetchInterval: (query) => (query.state.data?.historyPending ? 5000 : false),
  })
}

export const useErasePerson = () => {
  const queryClient = useQueryClient()
  const { call } = useAuthedFetch()
  return useMutation({
    mutationFn: ({ orgId = null, email, personRef, confirmEmail }) =>
      call(erasePersonData, { orgId: orgId ?? undefined, email, personRef, confirmEmail }),
    onSuccess: () => invalidatePeople(queryClient),
  })
}

export const useAccessLog = ({ orgId = null, action = null, enabled = true } = {}) => {
  const { userId, call } = useAuthedFetch()
  const base = { orgId: orgId ?? undefined, ...(action ? { action } : {}), limit: 50 }
  return useInfiniteQuery({
    queryKey: [...P2_KEYS.ACCESS_LOG(base), userId],
    queryFn: ({ pageParam }) => call(getKpiAccessLog, { ...base, ...(pageParam ? { cursor: pageParam } : {}) }),
    initialPageParam: null,
    getNextPageParam: (last) => last?.nextCursor ?? null,
    enabled: !!userId && enabled,
    staleTime: 0,
  })
}

// ─── KPI P3 frameworks ───────────────────────────────────────────────────────
export const P3_KEYS = {
  FRAMEWORKS: (orgId) => ["KPI_FRAMEWORKS", orgId ?? "own"],
  SCENARIO_MAP: (orgId, scenarioId) => ["KPI_SCENARIO_FRAMEWORK_MAP", orgId ?? "own", scenarioId],
}

export const useKpiFrameworks = ({ orgId = null, enabled = true } = {}) => {
  const { userId, call } = useAuthedFetch()
  return useQuery({
    queryKey: [...P3_KEYS.FRAMEWORKS(orgId), userId],
    queryFn: () => call(getKpiFrameworks, { orgId: orgId ?? undefined }),
    enabled: !!userId && enabled,
    staleTime: 60 * 1000,
    retry: (count, err) => count < 2 && ![400, 403, 404].includes(err?.status),
  })
}

const invalidateFrameworks = (queryClient) => {
  for (const key of ["KPI_FRAMEWORKS", "KPI_SCENARIO_FRAMEWORK_MAP", "KPI_DASHBOARD"]) queryClient.invalidateQueries({ queryKey: [key] })
}

export const useUpdateFrameworkSettings = () => {
  const queryClient = useQueryClient()
  const { call } = useAuthedFetch()
  return useMutation({
    mutationFn: ({ orgId = null, enabled, pins, targets }) => call(updateKpiFrameworkSettings, { orgId: orgId ?? undefined, enabled, pins, targets }),
    onSuccess: () => invalidateFrameworks(queryClient),
  })
}

export const useSaveCustomFramework = () => {
  const queryClient = useQueryClient()
  const { call } = useAuthedFetch()
  return useMutation({
    mutationFn: ({ orgId = null, framework, file = null, dryRun = false }) =>
      call(saveKpiCustomFramework, { orgId: orgId ?? undefined, framework, ...(file ? { file } : {}), dryRun }),
    onSuccess: (out, vars) => {
      if (!vars.dryRun && out?.valid) invalidateFrameworks(queryClient)
    },
  })
}

export const useDeleteCustomFramework = () => {
  const queryClient = useQueryClient()
  const { call } = useAuthedFetch()
  return useMutation({
    mutationFn: ({ orgId = null, id }) => call(deleteKpiCustomFramework, { orgId: orgId ?? undefined, id }),
    onSuccess: () => invalidateFrameworks(queryClient),
  })
}

export const useScenarioFrameworkMap = ({ orgId = null, scenarioId, enabled = true } = {}) => {
  const { userId, call } = useAuthedFetch()
  return useQuery({
    queryKey: [...P3_KEYS.SCENARIO_MAP(orgId, scenarioId), userId],
    queryFn: () => call(getScenarioFrameworkMap, { orgId: orgId ?? undefined, scenarioId }),
    enabled: !!userId && !!scenarioId && enabled,
    staleTime: 60 * 1000,
    retry: (count, err) => count < 2 && ![400, 403, 404].includes(err?.status),
  })
}

// exercise.frameworkRate by frameworkCategory ("fwId/catId") under the dashboard's filters.
export const useFrameworkRates = ({ orgId = null, filters = {}, enabled = true } = {}) => {
  const { userId, call } = useAuthedFetch()
  const body = {
    orgId: orgId ?? undefined,
    filters,
    queries: [{ id: "fw", metric: "exercise.frameworkRate", groupBy: ["frameworkCategory"], limit: 500 }],
  }
  return useQuery({
    queryKey: ["KPI_DASHBOARD", "frameworks", userId, JSON.stringify(body)],
    queryFn: () => call(fetchKpis, body),
    enabled: !!userId && enabled,
    staleTime: 60 * 1000,
    placeholderData: keepPreviousData,
    retry: (count, err) => count < 2 && ![400, 403, 404].includes(err?.status),
  })
}

// ─── KPI P4 dashboard views and export ───────────────────────────────────────
export const useDashboardViews = ({ orgId = null, enabled = true } = {}) => {
  const { userId, call } = useAuthedFetch()
  return useQuery({
    queryKey: ["KPI_DASHBOARD_VIEWS", orgId ?? "own", userId],
    queryFn: () => call(getKpiDashboardViews, { orgId: orgId ?? undefined }),
    enabled: !!userId && enabled,
    staleTime: 5 * 60 * 1000,
    retry: (count, err) => count < 2 && ![400, 403, 404].includes(err?.status),
  })
}

export const useSaveDashboardView = () => {
  const queryClient = useQueryClient()
  const { call } = useAuthedFetch()
  return useMutation({
    mutationFn: ({ orgId = null, view }) => call(saveKpiDashboardView, { orgId: orgId ?? undefined, view }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["KPI_DASHBOARD_VIEWS"] }),
  })
}

export const useDeleteDashboardView = () => {
  const queryClient = useQueryClient()
  const { call } = useAuthedFetch()
  return useMutation({
    mutationFn: ({ orgId = null, id }) => call(deleteKpiDashboardView, { orgId: orgId ?? undefined, id }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["KPI_DASHBOARD_VIEWS"] }),
  })
}

export const useExportDashboard = () => {
  const { call } = useAuthedFetch()
  return useMutation({ mutationFn: ({ orgId = null, ...args }) => call(exportKpiDashboard, { orgId: orgId ?? undefined, ...args }) })
}

export const useExportReport = () => {
  const { call } = useAuthedFetch()
  return useMutation({
    mutationFn: ({ orgId = null, format, run, against = null, attackType = null, baseline = null }) =>
      call(exportKpiReport, {
        orgId: orgId ?? undefined,
        format,
        run,
        ...(against && against !== "scenario" ? { against, attackType: attackType ?? undefined, baseline: baseline ?? undefined } : {}),
      }),
  })
}

// Platform admins: confirm or change a scenario's attack types; refreshes every comparison.
export const useSetScenarioAttackTypes = () => {
  const queryClient = useQueryClient()
  const { call } = useAuthedFetch()
  return useMutation({
    mutationFn: ({ scenarioId, attackTypes }) => call(setScenarioAttackTypes, { scenarioId, attackTypes }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["KPI_COMPARE_RUNS"] }),
  })
}

export const useExportPerson = () => {
  const { call } = useAuthedFetch()
  return useMutation({ mutationFn: ({ orgId = null, format, personRef }) => call(exportKpiPerson, { orgId: orgId ?? undefined, format, personRef }) })
}
